/**
 * A long coded list (diagnoses) picked by typing: a few letters of the name, a synonym or the code
 * find it; picked items show as chips that can be removed. Works offline (the list is in the pack).
 * A `multi` concept keeps several; a `choice` concept keeps one.
 */
import type { ConceptChoice, ConceptDef } from '@assist/core';
import { useMemo, useState } from 'preact/hooks';
import { t } from '../i18n.js';

const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');

/** Best matches first: a code or word that starts with what was typed, then anywhere. */
export function searchChoices(choices: ConceptChoice[], q: string, max = 12): ConceptChoice[] {
  const words = norm(q).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const scored: Array<[number, ConceptChoice]> = [];
  for (const ch of choices) {
    const hay = norm([ch.display, ch.icd11 ?? '', ...(ch.synonyms ?? [])].join(' '));
    if (!words.every((w) => hay.includes(w))) continue;
    const first = words[0] as string;
    const score =
      (norm(ch.icd11 ?? '').startsWith(first) ? 0 : 2) +
      (new RegExp(`(^|\\s)${first.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(hay) ? 0 : 1);
    scored.push([score, ch]);
  }
  return scored
    .sort((a, b) => a[0] - b[0] || a[1].display.localeCompare(b[1].display))
    .slice(0, max)
    .map((x) => x[1]);
}

export function SearchPicker(props: {
  id: string;
  concept: ConceptDef;
  entry: string | undefined;
  onChange: (e: string | undefined) => void;
}) {
  const c = props.concept;
  const many = c.type === 'multi';
  const [q, setQ] = useState('');
  const entry = props.entry ?? '';
  const picked = entry.split(',').filter(Boolean);
  const byCode = useMemo(
    () => new Map((c.choices ?? []).map((x) => [String(x.code), x])),
    [c.choices],
  );
  const hits = useMemo(() => {
    const taken = new Set(entry.split(',').filter(Boolean));
    return searchChoices(c.choices ?? [], q).filter((x) => !taken.has(String(x.code)));
  }, [c.choices, q, entry]);
  const set = (codes: string[]) => props.onChange(codes.length ? codes.join(',') : undefined);
  const add = (code: string) => {
    set(many ? [...picked, code] : [code]);
    setQ('');
  };
  return (
    <div class="picker" data-search={c.code}>
      {picked.length ? (
        <ul class="chips">
          {picked.map((code) => {
            const ch = byCode.get(code);
            return (
              <li key={code}>
                <span>
                  {ch?.display ?? code}
                  {ch?.icd11 ? <span class="muted"> · {ch.icd11}</span> : null}
                </span>
                <button
                  type="button"
                  class="chip-x"
                  aria-label={t.removeChoice(ch?.display ?? code)}
                  onClick={() => set(picked.filter((x) => x !== code))}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {many || !picked.length ? (
        <input
          id={props.id}
          type="search"
          autocomplete="off"
          placeholder={t.searchChoices}
          value={q}
          onInput={(e) => setQ(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && hits[0]) {
              e.preventDefault();
              add(String(hits[0].code));
            }
          }}
        />
      ) : null}
      {q && hits.length ? (
        <ul class="hits">
          {hits.map((x) => (
            <li key={x.code}>
              <button type="button" onClick={() => add(String(x.code))}>
                {x.display}
                {x.icd11 ? <span class="muted"> · {x.icd11}</span> : null}
                {x.group ? <span class="muted small"> — {x.group}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : q ? (
        <p class="help">{t.noMatch}</p>
      ) : null}
    </div>
  );
}
